/**
 * Ajouter un enfant.
 *
 * ## Ce que ce parcours corrige
 *
 * Le précédent avait six étapes, une par table de la base, et n'écrivait rien
 * avant le tout dernier clic. Trois défauts en découlaient, et ce sont eux qui
 * ont motivé la réécriture :
 *
 * 1. **Tout se perdait.** Fermer l'onglet après cinq écrans, c'était
 *    recommencer. Le brouillon local y répond : la fiche survit au
 *    rafraîchissement, à la batterie vide et au coup de téléphone.
 *
 * 2. **L'enregistrement pouvait réussir à moitié.** L'enfant était créé, puis
 *    cinq `upsert` partaient sans que leur erreur soit jamais lue. Une fiche
 *    santé vide le jour du stage ressemblait à un oubli du parent ; c'en était
 *    un du code. Ici chaque écriture est vérifiée, et un échec efface l'enfant
 *    créé plutôt que de laisser une fiche à trous.
 *
 * 3. **L'enregistrement partait deux fois.** `handleSubmit()` était appelé DANS
 *    un `setFormData(prev => ...)` ; en mode strict, React exécute ce
 *    réducteur deux fois. Le garde-fou est maintenant une référence, posée
 *    avant tout `await` — un booléen d'état arrive trop tard.
 *
 * ## Pourquoi le brouillon reste local
 *
 * Créer la fiche en base dès la première étape aurait permis de la reprendre
 * d'un autre appareil. Mais un enfant « en cours de saisie » apparaîtrait
 * aussitôt dans les listes des coordinateurs et des animateurs, qui n'ont pas
 * demandé à voir des fiches à moitié remplies. Le brouillon reste donc dans le
 * navigateur, et la base ne reçoit que des fiches entières.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { AlertCircle, Trash2 } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAuthStore } from '../../stores/authStore';
import { useKidStore } from '../../stores/kidStore';
import { useSchoolStore } from '../../stores/schoolStore';
import { useAuthorizedPersonsStore } from '../../stores/authorizedPersonsStore';
import { VERSION_POLITIQUE } from '../../lib/confidentialite';
import { completudeProfil } from '../../lib/completude';
import { CLASSE_HORS_LISTE, anneeScolaireCourante, resumeClasse, type ClassesConnues } from '../../lib/classes/regles';
import { enregistrerClasse, lireClassesConnues, signalerClasseManquante } from '../../lib/classes/gateway.supabase';
import LigneClasse from '../../components/forms/kid/LigneClasse';

import Etapes from '../../components/forms/kid/Etapes';
import EtapeIdentite, { type FratrieProposee } from '../../components/forms/kid/EtapeIdentite';
import EtapeSante from '../../components/forms/kid/EtapeSante';
import EtapeVie from '../../components/forms/kid/EtapeVie';
import EtapeSorties from '../../components/forms/kid/EtapeSorties';
import EtapeAutorisations from '../../components/forms/kid/EtapeAutorisations';
import Recapitulatif from '../../components/forms/kid/Recapitulatif';
import BandeauDesManques from '../../components/forms/kid/BandeauDesManques';
import { MentionObligatoire } from '../../components/forms/kid/champs';
import {
  champsEnDefaut,
  manquesSignales,
  nombresParEtape,
  reunir,
  type Signales,
} from '../../components/forms/kid/manques';
import {
  ETAPES,
  FICHE_VIDE,
  erreursEtape,
  premiereEtapeIncomplete,
  adresseDuProfil,
  ecoleEnregistree,
  memeAdresse,
  versLignes,
  type ContexteFiche,
  type Erreurs,
  type Etape,
  type Fiche,
} from '../../components/forms/kid/fiche';

/*
  Le nombre annoncé suit la liste des étapes : « Cinq étapes » était resté
  écrit après l'ajout du récapitulatif, qui en fait six.
*/
const EN_TOUTES_LETTRES = ['Zéro', 'Une', 'Deux', 'Trois', 'Quatre', 'Cinq', 'Six', 'Sept', 'Huit'];
const NOMBRE_ETAPES = EN_TOUTES_LETTRES[ETAPES.length] ?? String(ETAPES.length);

const cleBrouillon = (userId: string) => `cdv:fiche-enfant:${userId}`;

const NewKidPage = () => {
  const navigate = useNavigate();
  const { user, profile, profileLoaded } = useAuthStore();
  const { kids, fetchKids } = useKidStore();
  const { schools, fetchSchools } = useSchoolStore();
  const { persons, fetchPersons } = useAuthorizedPersonsStore();

  const [fiche, setFiche] = useState<Fiche>(FICHE_VIDE);
  const [photo, setPhoto] = useState<File | null>(null);
  const [etape, setEtape] = useState<Etape>('identite');
  const [atteintes, setAtteintes] = useState<Etape[]>(['identite']);
  /*
    Les champs signalés au parent (« Continuer » refusé, enregistrement
    refusé) ; leurs messages suivent la fiche et partent dès que le champ est
    rempli (`manques.ts`).
  */
  const [signales, setSignales] = useState<Signales>({});
  /* Les champs en défaut au dernier enregistrement refusé, `null` tant qu'aucun ne l'a été. */
  const [refus, setRefus] = useState<Signales | null>(null);
  const [enregistrement, setEnregistrement] = useState(false);
  const [echec, setEchec] = useState<string | null>(null);
  const [brouillonRepris, setBrouillonRepris] = useState(false);
  /* Les centres reliés aux écoles et leurs classes : la classe n'est demandée qu'aux écoles qui en ont. */
  const [classesConnues, setClassesConnues] = useState<ClassesConnues | undefined>(undefined);
  const annee = useMemo(() => anneeScolaireCourante(), []);

  /*
    Le garde-fou du double envoi. Une référence, pas un état : `setEnregistrement`
    ne prend effet qu'au rendu suivant, donc deux appels rapprochés passeraient
    tous les deux la condition.
  */
  const enCours = useRef(false);

  /* Le pré-remplissage de l'adresse : fait une fois, jamais sur un brouillon. */
  const brouillonTrouve = useRef(false);
  const adresseProposee = useRef(false);
  const ficheProposee = useRef<Fiche | null>(null);

  useEffect(() => {
    fetchKids();
    fetchSchools();
    fetchPersons();
  }, [fetchKids, fetchSchools, fetchPersons]);

  useEffect(() => {
    let annule = false;
    lireClassesConnues()
      .then((c) => {
        if (!annule) setClassesConnues(c);
      })
      .catch((e) => console.error('Classes des centres illisibles :', e));
    return () => {
      annule = true;
    };
  }, []);

  /* --- Le brouillon ------------------------------------------------------ */

  useEffect(() => {
    if (!user) return;
    try {
      const brut = localStorage.getItem(cleBrouillon(user.id));
      if (!brut) return;
      const enregistre = JSON.parse(brut) as { fiche: Fiche; etape: Etape };
      if (!enregistre?.fiche) return;
      brouillonTrouve.current = true;
      /* Fusion et non remplacement : un brouillon d'une version antérieure du
         formulaire n'a pas forcément tous les champs, et une fiche à laquelle
         il manque une clé casse les composants qui la lisent. */
      setFiche({
        identite: { ...FICHE_VIDE.identite, ...enregistre.fiche.identite },
        sante: { ...FICHE_VIDE.sante, ...enregistre.fiche.sante },
        vie: { ...FICHE_VIDE.vie, ...enregistre.fiche.vie },
        sorties: { ...FICHE_VIDE.sorties, ...enregistre.fiche.sorties },
        autorisations: {
          ...FICHE_VIDE.autorisations,
          ...enregistre.fiche.autorisations,
          image: { ...FICHE_VIDE.autorisations.image, ...enregistre.fiche.autorisations?.image },
        },
      });
      if (ETAPES.some((e) => e.cle === enregistre.etape)) {
        setEtape(enregistre.etape);
        const jusqua = ETAPES.findIndex((e) => e.cle === enregistre.etape);
        setAtteintes(ETAPES.slice(0, jusqua + 1).map((e) => e.cle));
      }
      setBrouillonRepris(true);
    } catch {
      /* Un brouillon illisible n'est pas une panne : on repart d'une fiche vide. */
    }
  }, [user]);

  /*
    Le numéro de registre national du parent et ceux des autres enfants : la
    fiche ne peut reprendre aucun d'eux (l'aîné recopié sur le cadet).
  */
  const contexte = useMemo<ContexteFiche>(
    () => ({
      nrnInterdits: [profile?.nnational ?? '', ...kids.map((k) => k.n_national ?? '')].filter(Boolean),
      classes: classesConnues,
    }),
    [profile, kids, classesConnues],
  );

  /*
    L'adresse du parent, reprise d'office dans une fiche neuve : il l'a donnée
    dans « Vos coordonnées » juste avant. Une seule fois, et jamais par-dessus
    un brouillon — un parent qui avait décoché la case ne la retrouve pas
    cochée en revenant.

    Un parent seulement : l'enfant d'une organisation (CPAS, maison d'accueil)
    ou d'un proche n'habite pas forcément à son adresse. La case reste
    proposée, décochée.
  */
  useEffect(() => {
    const adresse = profile?.account_type === 'parent' ? adresseDuProfil(profile) : null;
    if (!adresse || adresseProposee.current || brouillonTrouve.current) return;
    adresseProposee.current = true;
    // Construite hors de la mise à jour : la référence comparée plus bas doit être celle gardée
    const proposee = {
      ...FICHE_VIDE,
      identite: { ...FICHE_VIDE.identite, ...adresse, memeAdresseQueParent: true },
    };
    ficheProposee.current = proposee;
    setFiche((f) => (f === FICHE_VIDE ? proposee : f));
  }, [profile]);

  useEffect(() => {
    if (!user) return;
    // Une fiche vide, ou seulement pré-remplie, n'est pas un brouillon à retrouver
    if (fiche === FICHE_VIDE || fiche === ficheProposee.current) return;
    try {
      localStorage.setItem(cleBrouillon(user.id), JSON.stringify({ fiche, etape }));
    } catch {
      /* Navigation privée, quota plein : le formulaire marche, sans filet. */
    }
  }, [user, fiche, etape]);

  const effacerBrouillon = useCallback(() => {
    if (!user) return;
    try {
      localStorage.removeItem(cleBrouillon(user.id));
    } catch {
      /* Rien à faire : la fiche est de toute façon remise à zéro en mémoire. */
    }
  }, [user]);

  const recommencer = () => {
    effacerBrouillon();
    // Repartir de zéro garde l'adresse du parent proposée, comme une fiche neuve
    const adresse = profile?.account_type === 'parent' ? adresseDuProfil(profile) : null;
    const neuve = adresse
      ? { ...FICHE_VIDE, identite: { ...FICHE_VIDE.identite, ...adresse, memeAdresseQueParent: true } }
      : FICHE_VIDE;
    ficheProposee.current = adresse ? neuve : null;
    setFiche(neuve);
    setPhoto(null);
    setEtape('identite');
    setAtteintes(['identite']);
    setSignales({});
    setRefus(null);
    setBrouillonRepris(false);
  };

  /* --- Navigation entre étapes ------------------------------------------ */

  const allerA = (cible: Etape) => {
    setEchec(null);
    setEtape(cible);
    setAtteintes((a) => (a.includes(cible) ? a : [...a, cible]));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const continuer = () => {
    const trouvees = erreursEtape(etape, fiche, contexte);
    if (Object.keys(trouvees).length > 0) {
      setSignales((s) => reunir(s, { [etape]: Object.keys(trouvees) }));
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    const i = ETAPES.findIndex((e) => e.cle === etape);
    allerA(ETAPES[Math.min(i + 1, ETAPES.length - 1)].cle);
  };

  const revenir = () => {
    const i = ETAPES.findIndex((e) => e.cle === etape);
    allerA(ETAPES[Math.max(i - 1, 0)].cle);
  };

  /* --- Reprendre les informations d'un frère ou d'une sœur --------------- */

  const fratrie: FratrieProposee[] = useMemo(
    () => kids.map((k) => ({ id: k.id, prenom: k.prenom })),
    [kids],
  );

  const copierFratrie = (id: string) => {
    const source = kids.find((k) => k.id === id);
    if (!source) return;

    /* Une école libre n'a pas d'identifiant : elle repasse par « Autre ». */
    const connue = schools.some((e) => e.id === source.ecole);
    const ecole = source.ecole ? (connue ? source.ecole : 'other') : '';

    setFiche((f) => ({
      ...f,
      identite: {
        ...f.identite,
        adresse: source.adresse || '',
        cPostal: source.cpostal || '',
        localite: source.localite || '',
        memeAdresseQueParent: (() => {
          const parent = adresseDuProfil(profile);
          return (
            !!parent &&
            memeAdresse(parent, {
              adresse: source.adresse || '',
              cPostal: source.cpostal || '',
              localite: source.localite || '',
            })
          );
        })(),
        ecole,
        ecoleAutre: connue ? '' : source.ecole || '',
      },
      sante: {
        ...f.sante,
        medecinNom: source.health?.doctor_name || f.sante.medecinNom,
        medecinTel: source.health?.doctor_phone || f.sante.medecinTel,
      },
      sorties: {
        ...f.sorties,
        personnesIds: source.departure?.pickup_people_ids?.length
          ? source.departure.pickup_people_ids
          : f.sorties.personnesIds,
      },
    }));
  };

  /* --- L'enregistrement -------------------------------------------------- */

  const enregistrer = async () => {
    if (!user || enCours.current) return;

    /*
      Dernier filet : toutes les étapes, pas seulement la dernière visitée. Le
      parent est ramené à la première partie en cause, où le bandeau dit ce qui
      manque puis mène au plus court — plus de bandeau rouge général, qui
      restait sur toutes les étapes.
    */
    const incomplete = premiereEtapeIncomplete(fiche, contexte);
    if (incomplete) {
      const enDefaut = champsEnDefaut(fiche, contexte);
      setSignales((s) => reunir(s, enDefaut));
      setRefus(enDefaut);
      allerA(incomplete);
      return;
    }

    enCours.current = true;
    setEnregistrement(true);
    setEchec(null);

    let kidId: string | null = null;

    try {
      const lignes = versLignes(fiche, '', user.id, VERSION_POLITIQUE);

      const creation = await supabase
        .from('kids')
        .insert({ ...lignes.kids, user_id: user.id })
        .select('id')
        .single();

      if (creation.error || !creation.data) {
        throw new Error(creation.error?.message || "La fiche n'a pas pu être créée.");
      }
      /*
        Le client Supabase rend ici un type indécidable (il ne sait pas si la
        projection a abouti). La forme, elle, est certaine : `select('id')`.
      */
      const id = (creation.data as { id: string }).id;
      kidId = id;

      /*
        Les six tables filles, une par une et avec LEUR erreur lue. En parallèle
        elles seraient plus rapides, mais un `Promise.all` qui échoue laisse les
        autres écritures aboutir — exactement l'état à trous qu'on cherche à
        rendre impossible. Chacune porte son nom en clair : « kid_allergies :
        violates check constraint » n'apprend rien à une famille.
      */
      const t = versLignes(fiche, id, user.id, VERSION_POLITIQUE);
      const ecritures: { quoi: string; ecrire: () => PromiseLike<{ error: { message: string } | null }> }[] = [
        { quoi: 'la fiche santé', ecrire: () => supabase.from('kid_health').upsert(t.kid_health, { onConflict: 'kid_id' }) },
        { quoi: 'les allergies', ecrire: () => supabase.from('kid_allergies').upsert(t.kid_allergies, { onConflict: 'kid_id' }) },
        { quoi: 'les activités', ecrire: () => supabase.from('kid_activities').upsert(t.kid_activities, { onConflict: 'kid_id' }) },
        { quoi: 'les sorties', ecrire: () => supabase.from('kid_departure').upsert(t.kid_departure, { onConflict: 'kid_id' }) },
        { quoi: 'les besoins particuliers', ecrire: () => supabase.from('kid_inclusion').upsert(t.kid_inclusion, { onConflict: 'kid_id' }) },
        { quoi: 'les autorisations d’image', ecrire: () => supabase.from('kid_image_rights').upsert(t.kid_image_rights, { onConflict: 'kid_id' }) },
      ];

      for (const { quoi, ecrire } of ecritures) {
        const { error } = await ecrire();
        if (error) throw new Error(`${quoi} n'ont pas pu être enregistrées (${error.message})`);
      }

      /* La classe de l'année, dans `kid_classes` ; une classe signalée hors liste n'écrit rien. */
      const classeId = fiche.identite.classeId;
      if (classeId && classeId !== CLASSE_HORS_LISTE) {
        try {
          const ecole = ecoleEnregistree(fiche.identite);
          await enregistrerClasse(id, ecole, ecole, annee, classeId);
        } catch (e) {
          throw new Error(`la classe n'a pas pu être enregistrée (${e instanceof Error ? e.message : String(e)})`);
        }
      }

      /*
        La photo en dernier, et son échec n'annule rien : une fiche complète
        sans portrait vaut mieux qu'une fiche perdue parce qu'un JPEG a mal
        voyagé. Le parent pourra la reprendre depuis la fiche.
      */
      let photoManquee = false;
      if (photo) {
        const extension = photo.type === 'image/png' ? 'png' : 'jpg';
        const chemin = `${id}.${extension}`;
        const { error: erreurTeleversement } = await supabase.storage
          .from('kid-photos')
          .upload(chemin, photo, { upsert: true, contentType: photo.type });

        if (erreurTeleversement) {
          photoManquee = true;
        } else {
          const { error: erreurLien } = await supabase
            .from('kids')
            .update({ photo_url: chemin })
            .eq('id', id);
          if (erreurLien) photoManquee = true;
        }
      }

      effacerBrouillon();
      await fetchKids();
      /*
        Direction les stages, plus la fiche : le parent vient de relire son
        récapitulatif, et ce qu'il est venu faire, c'est choisir des semaines.
        L'enfant y est déjà coché ; sa fiche reste à un clic depuis le bandeau.
      */
      navigate('/activities', {
        state: {
          nouvelEnfant: { id, prenom: fiche.identite.prenom.trim() },
          photoManquee,
        },
      });
    } catch (err: unknown) {
      /*
        L'enfant a peut-être été créé avant l'échec. On le retire : la cascade
        emporte les lignes filles déjà écrites, et le parent retrouve son
        brouillon intact plutôt qu'une fiche à moitié née qu'il devra
        démêler.
      */
      if (kidId) {
        await supabase.from('kids').delete().eq('id', kidId);
      }
      setEchec(
        err instanceof Error
          ? `L'enregistrement a échoué : ${err.message}`
          : "L'enregistrement a échoué. Vos réponses sont conservées, réessayez dans un instant.",
      );
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      enCours.current = false;
      setEnregistrement(false);
    }
  };

  /* --- Rendu ------------------------------------------------------------- */

  /*
    Le parent d'abord : ses coordonnées servent à la fiche (l'adresse, la
    personne autorisée) et elles seraient de toute façon exigées avant de
    choisir un stage. Il les donne une fois, maintenant, puis revient ici.
  */
  if (profileLoaded && !completudeProfil(profile).complet) {
    return <Navigate to="/bienvenue?suite=/kids/new" replace />;
  }

  const titre = ETAPES.find((e) => e.cle === etape)?.titre ?? '';
  const manques = manquesSignales(fiche, contexte, signales);
  /** Les messages à afficher sur une étape : ce qui, parmi les champs signalés, manque encore. */
  const messages = (cle: Etape): Erreurs => manques[cle] ?? {};

  return (
    <div className="container mx-auto max-w-3xl px-4 py-8 md:py-12">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 md:text-3xl">Ajouter un enfant</h1>
        <p className="mt-1 text-gray-600">
          {NOMBRE_ETAPES} étapes, une dizaine de minutes. Vos réponses sont conservées si vous devez vous
          interrompre.
        </p>
      </div>

      {brouillonRepris && (
        <div className="mb-6 flex flex-col gap-3 rounded-xl border border-primary-200 bg-primary-50 p-4 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm text-gray-700">
            Nous avons retrouvé la fiche que vous aviez commencée.
            {photo === null && ' La photo, elle, est à rechoisir.'}
          </p>
          <button
            type="button"
            onClick={recommencer}
            className="inline-flex flex-shrink-0 items-center self-start text-sm font-medium text-gray-600 hover:text-red-600 sm:self-auto"
          >
            <Trash2 className="mr-1.5 h-3.5 w-3.5" />
            Repartir de zéro
          </button>
        </div>
      )}

      <Etapes courante={etape} atteintes={atteintes} onAller={allerA} aCompleter={nombresParEtape(manques)} />

      {echec && (
        <div className="mb-6 flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 p-4">
          <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-red-600" />
          <p className="text-sm text-red-800">{echec}</p>
        </div>
      )}

      <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm md:p-8">
        <div className="mb-6 flex flex-wrap items-baseline justify-between gap-2">
          <h2 className="hidden text-xl font-bold text-gray-900 sm:block">{titre}</h2>
          {etape !== 'recapitulatif' && <MentionObligatoire />}
        </div>

        {etape !== 'recapitulatif' && refus?.[etape] && (
          <BandeauDesManques
            manques={messages(etape)}
            prochaine={premiereEtapeIncomplete(fiche, contexte)}
            onAller={allerA}
          />
        )}

        {etape === 'identite' && (
          <EtapeIdentite
            valeur={fiche.identite}
            onChange={(identite) => setFiche((f) => ({ ...f, identite }))}
            erreurs={messages('identite')}
            onSuivant={continuer}
            photo={photo}
            onPhoto={setPhoto}
            fratrie={fratrie}
            onCopierFratrie={copierFratrie}
            classes={classesConnues}
            annee={annee}
            onSignalerClasse={(classe) =>
              signalerClasseManquante({
                ecoleId: fiche.identite.ecole,
                classe,
                enfant: { prenom: fiche.identite.prenom.trim(), nom: fiche.identite.nom.trim() },
              })
            }
          />
        )}

        {etape === 'sante' && (
          <EtapeSante
            valeur={fiche.sante}
            onChange={(sante) => setFiche((f) => ({ ...f, sante }))}
            erreurs={messages('sante')}
            onPrecedent={revenir}
            onSuivant={continuer}
            prenom={fiche.identite.prenom}
          />
        )}

        {etape === 'vie' && (
          <EtapeVie
            valeur={fiche.vie}
            onChange={(vie) => setFiche((f) => ({ ...f, vie }))}
            erreurs={messages('vie')}
            onPrecedent={revenir}
            onSuivant={continuer}
            prenom={fiche.identite.prenom}
          />
        )}

        {etape === 'sorties' && (
          <EtapeSorties
            valeur={fiche.sorties}
            onChange={(sorties) => setFiche((f) => ({ ...f, sorties }))}
            erreurs={messages('sorties')}
            onPrecedent={revenir}
            onSuivant={continuer}
            prenom={fiche.identite.prenom}
          />
        )}

        {etape === 'autorisations' && (
          <EtapeAutorisations
            valeur={fiche.autorisations}
            onChange={(autorisations) => setFiche((f) => ({ ...f, autorisations }))}
            erreurs={messages('autorisations')}
            onPrecedent={revenir}
            onSuivant={continuer}
            prenom={fiche.identite.prenom}
          />
        )}

        {etape === 'recapitulatif' && (
          <Recapitulatif
            fiche={fiche}
            photo={photo}
            ecoles={schools}
            personnes={persons}
            onModifier={allerA}
            onPrecedent={revenir}
            onEnregistrer={enregistrer}
            enCours={enregistrement}
            classe={(() => {
              const r = resumeClasse(fiche.identite.ecole, fiche.identite.classeId, classesConnues, annee);
              return r ? <LigneClasse resume={r} /> : undefined;
            })()}
          />
        )}
      </div>

      <p className="mt-6 text-center text-sm text-gray-500">
        <button type="button" onClick={() => navigate('/kids')} className="hover:text-gray-700 underline underline-offset-2">
          Quitter — la fiche restera en brouillon
        </button>
      </p>
    </div>
  );
};

export default NewKidPage;
